import { useForm, useNotification } from "@/hooks";
import {
    Button,
    FormLayout,
    HeaderActions,
    Heading,
    HtmlPreview,
    IconButton,
    Input,
    LoadingProgress,
    Modal,
    Panel,
    Select,
    Switch,
    Textarea,
} from "@/ui";
import { useMutation, useQuery } from "@apollo/react-hooks";
import _ from "lodash";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useHistory } from "react-router-dom";
import {
    MUTATION_SET_DEFAULT_NOTIFICATION,
    MUTATION_UPDATE_NOTIFICATION_SETTING,
    QUERY_GET_NOTIFICATION_SETTING,
} from "../../apollo/module-operations/notification_setting";
import Icon from "../../base/components/Icon";
import useMeta from "../../base/hooks/useMeta";
import {
    notificationSettingOptions,
    notificationSettingTypeOptions,
    VARIABLES_TEMP_BUSSINES_CONTACT,
    VARIABLES_TEMP_DEFAULT,
} from "../../config/constant";
import ChooseEmailThemeModal from "./email-themes/ChooseEmailThemeModal";
import { hasEmailThemeTemplate } from "./email-themes/skins";
import { prepareEmailHtmlForAdminPreview } from "./email-themes/utils/prepareEmailHtmlForAdminPreview";
import styles from "./NotificationSettingForm.module.scss";

const SaveIcon = () => <Icon icon="save" />;
const RefreshIcon = () => <Icon icon="refresh" />;
const CopyIcon = () => <Icon icon="copy" />;

/**
 * Sanity checks for from_tx_email / email_notification_active as local drafts:
 * - Editing only from_tx_email (or email_notification_active) => Unsaved Changes modal when navigating away
 * - Save and Continue => both persist to store as before
 * - Leave Without Saving => both revert to initial
 */
const NotificationSettingForm = ({
    createOrUpdate,
    loading,
    inputs: inputStore,
    inputChange: inputStoreChange,
    onTabChangeAttempt,
}) => {
    const { user } = useMeta();
    const notify = useNotification();
    const history = useHistory();

    const [isOpen, setIsOpen] = useState(false);
    const [isThemeModalOpen, setIsThemeModalOpen] = useState(false);
    const [showUnsavedWarning, setShowUnsavedWarning] = useState(false);
    const [showSetDefaultConfirm, setShowSetDefaultConfirm] = useState(false);
    const [pendingNavigation, setPendingNavigation] = useState(null);
    const [initialState, setInitialState] = useState(null);
    const [isDataLoaded, setIsDataLoaded] = useState(false);
    const [selectKey, setSelectKey] = useState(0);

    const { inputs, setInputs, inputChange, selectChange, genericChange } =
        useForm();

    const variables = useMemo(
        () => ({ account_id: user.accountId, store_id: user.storeId }),
        [user.accountId, user.storeId],
    );
    const handler = inputs.handler || notificationSettingOptions[0].value;

    const { loading: getLoading } = useQuery(QUERY_GET_NOTIFICATION_SETTING, {
        variables: { ...variables, handler },
        skip: user.loading || !user.accountId || !user.storeId,
        fetchPolicy: "network-only",
        onCompleted: resp => {
            const notificationSetting = resp?.getStoreNotificationSetting;
            if (notificationSetting) {
                const {
                    content,
                    handler,
                    type,
                    notification_active,
                    email_subject,
                } = resp.getStoreNotificationSetting;
                const newInputs = {
                    content,
                    handler,
                    type,
                    notification_active: Boolean(notification_active),
                    email_subject: email_subject ?? "",
                    from_tx_email: inputStore.from_tx_email ?? "",
                    email_notification_active: Boolean(
                        inputStore.email_notification_active,
                    ),
                };
                setInputs(newInputs);
                // Reset initial state when new data is loaded
                setInitialState({ ...newInputs });
                setIsDataLoaded(true);
            }
        },
        onError: error => {
            console.error(error);
            notify("Error getting selected notification settings", "error");
            setInputs({ ...inputs, content: "" });
            setIsDataLoaded(true);
        },
    });

    // Ref to store successfully saved values
    const savedValuesRef = useRef(null);
    // Ref to store default values temporarily before saving
    const pendingDefaultValuesRef = useRef(null);
    // Ref to track if update is being called from setDefault
    const isSettingDefaultRef = useRef(false);
    // Ref to track if update is being called from Apply email theme
    const isApplyingThemeRef = useRef(false);
    // Ref to store Promise resolve callback for save completion
    const saveCompletionRef = useRef(null);

    const [update, { loading: updateLoading }] = useMutation(
        MUTATION_UPDATE_NOTIFICATION_SETTING,
        {
            onCompleted: () => {
                // setDefault / Apply theme show their own notifications
                if (
                    !isSettingDefaultRef.current &&
                    !isApplyingThemeRef.current
                ) {
                    notify(
                        "Notification settings updated successfully",
                        "success",
                    );
                }
                // Reset initial state after successful save
                // Use saved values that are in savedValuesRef
                if (savedValuesRef.current) {
                    setInitialState(savedValuesRef.current);
                    savedValuesRef.current = null;
                }
                isSettingDefaultRef.current = false;
                isApplyingThemeRef.current = false;
                // Resolve the save completion Promise if it exists
                if (saveCompletionRef.current) {
                    saveCompletionRef.current();
                    saveCompletionRef.current = null;
                }
            },
            onError: error => {
                if (!isApplyingThemeRef.current) {
                    notify("Update notification setting", "error");
                }
                savedValuesRef.current = null;
                isSettingDefaultRef.current = false;
                isApplyingThemeRef.current = false;
                // Reject the save completion Promise if it exists
                if (saveCompletionRef.current) {
                    saveCompletionRef.current(error);
                    saveCompletionRef.current = null;
                }
            },
        },
    );

    // Function to save notification settings
    const handleSaveNotificationSettings = useCallback(async () => {
        try {
            // Save values we're going to send to reset initialState afterwards
            const valuesToSave = {
                content: inputs.content,
                notification_active: Boolean(inputs.notification_active),
                email_subject: inputs.email_subject ?? "",
                type: inputs.type,
                handler: handler,
                email_notification_active: Boolean(
                    inputs.email_notification_active,
                ),
                from_tx_email: inputs.from_tx_email ?? "",
            };
            savedValuesRef.current = valuesToSave;

            // Create a Promise that resolves when onCompleted is called
            // This ensures initialState is updated before continuing
            await new Promise((resolve, reject) => {
                let resolved = false;
                saveCompletionRef.current = error => {
                    if (resolved) return; // Prevent multiple calls
                    resolved = true;
                    if (error) {
                        reject(error);
                    } else {
                        resolve();
                    }
                };

                update({
                    variables: {
                        ...variables,
                        input: {
                            type: inputs.type,
                            handler,
                            content: inputs.content,
                            email_subject: inputs.email_subject,
                            notification_active: Boolean(
                                inputs.notification_active,
                            ),
                        },
                    },
                }).catch(error => {
                    // Handle network errors or immediate failures
                    if (saveCompletionRef.current && !resolved) {
                        resolved = true;
                        saveCompletionRef.current(error);
                        saveCompletionRef.current = null;
                    }
                });
            });

            return true;
        } catch (error) {
            savedValuesRef.current = null;
            saveCompletionRef.current = null;
            return false;
        }
    }, [
        update,
        variables,
        inputs.type,
        handler,
        inputs.content,
        inputs.email_subject,
        inputs.notification_active,
        inputs.email_notification_active,
        inputs.from_tx_email,
    ]);

    // Unified function to save both notification settings and store if needed
    const handleSaveAll = useCallback(async () => {
        // 1. Save notification settings first
        const saved = await handleSaveNotificationSettings();
        if (!saved) {
            return false;
        }

        // 2. Check if store fields (from_tx_email or email_notification_active) changed (draft lives in inputs)
        const hasStoreChanges =
            initialState &&
            ((initialState.from_tx_email ?? "") !==
                (inputs.from_tx_email ?? "") ||
                Boolean(initialState.email_notification_active) !==
                    Boolean(inputs.email_notification_active));

        // 3. Sync draft to store, then persist store if needed
        if (hasStoreChanges && createOrUpdate && inputStoreChange) {
            const inputOptions = {
                ...inputStore,
                from_tx_email: inputs.from_tx_email ?? "",
                email_notification_active: Boolean(
                    inputs.email_notification_active,
                ),
            };
            createOrUpdate(inputOptions);
        }

        return true;
    }, [
        handleSaveNotificationSettings,
        createOrUpdate,
        inputStoreChange,
        initialState,
        inputs.from_tx_email,
        inputs.email_notification_active,
    ]);

    const [setDefault, { loading: defaultLoading }] = useMutation(
        MUTATION_SET_DEFAULT_NOTIFICATION,
        {
            variables,
            onCompleted: async resp => {
                const defaultSetting = _.find(
                    resp.setDefaultNotificationSettings,
                    setting =>
                        setting.handler === inputs.handler &&
                        setting.type === inputs.type,
                );

                if (!defaultSetting) {
                    notify("Default settings not found", "error");
                    return;
                }

                const {
                    content,
                    type,
                    handler,
                    email_subject,
                    notification_active,
                } = defaultSetting;

                // Store default values temporarily (keep from_tx_email and email_notification_active from local draft)
                pendingDefaultValuesRef.current = {
                    content,
                    type,
                    handler,
                    email_subject: email_subject ?? "",
                    notification_active: Boolean(notification_active),
                    email_notification_active: Boolean(
                        inputs.email_notification_active,
                    ),
                    from_tx_email: inputs.from_tx_email ?? "",
                };

                // Update form inputs with default values (from_tx_email and email_notification_active stay as-is)
                setInputs(prev => ({
                    ...prev,
                    content,
                    type,
                    handler,
                    email_subject: email_subject ?? "",
                    notification_active: Boolean(notification_active),
                }));

                // Automatically save the default values
                try {
                    savedValuesRef.current = pendingDefaultValuesRef.current;
                    isSettingDefaultRef.current = true;

                    await update({
                        variables: {
                            ...variables,
                            input: {
                                type,
                                handler,
                                content,
                                email_subject: email_subject ?? "",
                                notification_active:
                                    Boolean(notification_active),
                            },
                        },
                    });

                    // Update initialState after successful save
                    // (notification is shown in update's onCompleted is handled by isSettingDefaultRef)
                    // But we show a specific message here
                    notify(
                        "Default values restored and saved successfully",
                        "success",
                    );
                    pendingDefaultValuesRef.current = null;
                } catch (error) {
                    notify("Error saving default values", "error");
                    savedValuesRef.current = null;
                    pendingDefaultValuesRef.current = null;
                    isSettingDefaultRef.current = false;
                }
            },
            onError: () => {
                notify("Error loading default settings", "error");
                pendingDefaultValuesRef.current = null;
            },
        },
    );

    /** Display-only: expand Go placeholders with shared mock (does not mutate content). */
    const previewHtml = useMemo(
        () =>
            prepareEmailHtmlForAdminPreview(inputs.content, {
                store: inputStore,
            }),
        [inputStore, inputs.content],
    );

    const handleApplyEmailTheme = useCallback(
        async ({ html, handler: appliedHandler }) => {
            const targetHandler = appliedHandler || handler;
            const appliedLabel =
                notificationSettingOptions.find(
                    item => item.value === targetHandler,
                )?.label ?? "Email";

            // Keep editor in sync immediately; switch form handler only after save
            // so QUERY_GET does not fetch the target type before this mutation lands.
            genericChange(html, "content");
            setInputs(prev => ({ ...prev, content: html }));

            const valuesToSave = {
                content: html,
                notification_active: Boolean(inputs.notification_active),
                email_subject: inputs.email_subject ?? "",
                type: inputs.type,
                handler: targetHandler,
                email_notification_active: Boolean(
                    inputs.email_notification_active,
                ),
                from_tx_email: inputs.from_tx_email ?? "",
            };
            savedValuesRef.current = valuesToSave;
            isApplyingThemeRef.current = true;

            try {
                await new Promise((resolve, reject) => {
                    let resolved = false;
                    saveCompletionRef.current = error => {
                        if (resolved) return;
                        resolved = true;
                        if (error) {
                            reject(error);
                        } else {
                            resolve();
                        }
                    };

                    update({
                        variables: {
                            ...variables,
                            input: {
                                type: inputs.type,
                                handler: targetHandler,
                                content: html,
                                email_subject: inputs.email_subject,
                                notification_active: Boolean(
                                    inputs.notification_active,
                                ),
                            },
                        },
                    }).catch(error => {
                        if (saveCompletionRef.current && !resolved) {
                            resolved = true;
                            saveCompletionRef.current(error);
                            saveCompletionRef.current = null;
                        }
                    });
                });

                if (targetHandler !== handler) {
                    setInputs(prev => ({
                        ...prev,
                        content: html,
                        handler: targetHandler,
                    }));
                }

                notify(
                    `Theme applied and saved to ${appliedLabel}.`,
                    "success",
                );
            } catch (error) {
                console.error(error);
                isApplyingThemeRef.current = false;
                savedValuesRef.current = null;
                notify(
                    "Theme was applied in the editor but could not be saved. Click Update to retry.",
                    "error",
                );
            }
        },
        [
            genericChange,
            handler,
            inputs.email_notification_active,
            inputs.email_subject,
            inputs.from_tx_email,
            inputs.notification_active,
            inputs.type,
            notify,
            setInputs,
            update,
            variables,
        ],
    );

    const toggleModal = useCallback(() => {
        setIsOpen(prev => !prev);
    }, []);

    const clipboardCopy = useCallback(
        text => {
            navigator.clipboard.writeText(text);
            notify(`${text} is copied`, "success");
        },
        [notify],
    );

    const listVariables = useMemo(() => {
        return inputs.handler === "contact/business_owner"
            ? VARIABLES_TEMP_BUSSINES_CONTACT
            : VARIABLES_TEMP_DEFAULT;
    }, [inputs.handler]);

    // Detect unsaved changes (from_tx_email and email_notification_active are local drafts in inputs)
    const hasUnsavedChanges = useMemo(() => {
        if (!initialState || !isDataLoaded) return false;

        return (
            initialState.content !== inputs.content ||
            Boolean(initialState.notification_active) !==
                Boolean(inputs.notification_active) ||
            (initialState.email_subject ?? "") !==
                (inputs.email_subject ?? "") ||
            initialState.type !== inputs.type ||
            initialState.handler !== inputs.handler ||
            (initialState.from_tx_email ?? "") !==
                (inputs.from_tx_email ?? "") ||
            Boolean(initialState.email_notification_active) !==
                Boolean(inputs.email_notification_active)
        );
    }, [inputs, initialState, isDataLoaded]);

    // Expose function to verify if tab change is allowed
    useEffect(() => {
        if (onTabChangeAttempt) {
            onTabChangeAttempt((newTab, proceedCallback) => {
                if (hasUnsavedChanges) {
                    setPendingNavigation(() => () => {
                        if (proceedCallback) {
                            proceedCallback();
                        }
                    });
                    setShowUnsavedWarning(true);
                    return false; // Block tab change
                }
                return true; // Allow tab change
            });
        }
    }, [onTabChangeAttempt, hasUnsavedChanges]);

    // Guard for Email Type dropdown change (handler)
    const pendingHandlerChangeRef = useRef(null);

    const handleHandlerChange = useCallback(
        newHandler => {
            if (hasUnsavedChanges) {
                pendingHandlerChangeRef.current = newHandler;
                setShowUnsavedWarning(true);
            } else {
                selectChange({ value: newHandler }, "handler");
            }
        },
        [hasUnsavedChanges, selectChange],
    );

    // Guard for React Router navigation
    useEffect(() => {
        if (!hasUnsavedChanges) return;

        const unblock = history.block((location, action) => {
            if (action === "PUSH" || action === "REPLACE") {
                setPendingNavigation(() => () => {
                    unblock();
                    history.push(location.pathname);
                });
                setShowUnsavedWarning(true);
                return false;
            }
            return true;
        });

        return () => {
            unblock();
        };
    }, [hasUnsavedChanges, history]);

    const isLoading = updateLoading || getLoading || defaultLoading;

    // Helper function to close unsaved warning modal and clean up
    const closeUnsavedWarningModal = useCallback(() => {
        setShowUnsavedWarning(false);
        setPendingNavigation(null);
        pendingHandlerChangeRef.current = null;
    }, []);

    // Helper function to restore store fields to initial state (only used for fields still in store; from_tx_email and email_notification_active are local drafts)
    const restoreStoreFields = useCallback(() => {
        if (!initialState) return;
        // from_tx_email and email_notification_active are reverted via restoreLocalInputs
    }, []);

    // Helper function to restore local inputs to initial state
    const restoreLocalInputs = useCallback(() => {
        if (!initialState) return;

        setInputs({
            content: initialState.content,
            handler: initialState.handler,
            type: initialState.type,
            notification_active: Boolean(initialState.notification_active),
            email_subject: initialState.email_subject ?? "",
            from_tx_email: initialState.from_tx_email ?? "",
            email_notification_active: Boolean(
                initialState.email_notification_active,
            ),
        });
    }, [initialState, setInputs]);

    // Handler for "Save and Continue" button in modal
    const handleSaveAndContinue = useCallback(async () => {
        const saved = await handleSaveAll();
        if (!saved) return;

        closeUnsavedWarningModal();

        // Handle pending handler change
        if (pendingHandlerChangeRef.current) {
            const handlerToChange = pendingHandlerChangeRef.current;
            pendingHandlerChangeRef.current = null;
            setSelectKey(prev => prev + 1);
            setTimeout(() => {
                selectChange({ value: handlerToChange }, "handler");
            }, 0);
        }

        // Execute pending navigation
        if (pendingNavigation) {
            setTimeout(() => {
                pendingNavigation();
                setPendingNavigation(null);
            }, 0);
        }
    }, [
        handleSaveAll,
        closeUnsavedWarningModal,
        selectChange,
        pendingNavigation,
    ]);

    // Handler for "Leave Without Saving" button in modal
    const handleLeaveWithoutSaving = useCallback(() => {
        const newHandler = pendingHandlerChangeRef.current;
        const hasHandlerChange =
            newHandler && newHandler !== initialState?.handler;

        // Restore store fields
        restoreStoreFields();

        // Close modal and clear pending handler
        closeUnsavedWarningModal();

        // Handle handler change or restore local inputs
        if (hasHandlerChange) {
            setSelectKey(prev => prev + 1);
            setTimeout(() => {
                selectChange({ value: newHandler }, "handler");
            }, 0);
        } else {
            restoreLocalInputs();
        }

        // Execute pending navigation
        if (pendingNavigation) {
            setTimeout(() => {
                pendingNavigation();
                setPendingNavigation(null);
            }, 0);
        }
    }, [
        initialState,
        restoreStoreFields,
        restoreLocalInputs,
        closeUnsavedWarningModal,
        selectChange,
        pendingNavigation,
    ]);

    // Warning modal for unsaved changes
    const UnsavedChangesModal = () => (
        <Modal
            description='Your changes have not been saved yet. Click "Save and Continue" to save your notification settings.'
            footer={
                <div className={styles.modalActions}>
                    <Button
                        loading={updateLoading}
                        onClick={handleSaveAndContinue}
                    >
                        Save and Continue
                    </Button>
                    <Button
                        disabled={updateLoading}
                        onClick={handleLeaveWithoutSaving}
                        variant="secondary"
                    >
                        Leave Without Saving
                    </Button>
                </div>
            }
            onOpenChange={open => {
                if (!open) closeUnsavedWarningModal();
            }}
            open={showUnsavedWarning}
            title="Unsaved Changes"
        >
            {null}
        </Modal>
    );

    const closeSetDefaultConfirmModal = useCallback(() => {
        setShowSetDefaultConfirm(false);
    }, []);

    const handleSetDefaultConfirm = useCallback(() => {
        closeSetDefaultConfirmModal();
        setDefault();
    }, [closeSetDefaultConfirmModal, setDefault]);

    const handleSetDefaultClick = useCallback(() => {
        if (hasUnsavedChanges) {
            setShowSetDefaultConfirm(true);
        } else {
            setDefault();
        }
    }, [hasUnsavedChanges, setDefault]);

    const shouldShowUpdateSpinner = useMemo(
        () =>
            !(defaultLoading || isSettingDefaultRef.current || getLoading) &&
            (loading || updateLoading),
        [defaultLoading, getLoading, loading, updateLoading],
    );

    const shouldShowUpdateIcon = useMemo(
        () =>
            defaultLoading ||
            isSettingDefaultRef.current ||
            getLoading ||
            !(loading || isLoading),
        [defaultLoading, getLoading, isLoading, loading],
    );

    // Confirmation modal for Set Defaults when there are unsaved changes
    const SetDefaultConfirmModal = () => (
        <Modal
            description="You have unsaved changes. Do you want to discard them and restore the default values? The defaults will be saved automatically."
            footer={
                <div className={styles.modalActions}>
                    <Button
                        loading={defaultLoading || updateLoading}
                        onClick={handleSetDefaultConfirm}
                    >
                        Restore and Save
                    </Button>
                    <Button
                        disabled={defaultLoading || updateLoading}
                        onClick={closeSetDefaultConfirmModal}
                        variant="secondary"
                    >
                        Cancel
                    </Button>
                </div>
            }
            onOpenChange={open => {
                if (!open) closeSetDefaultConfirmModal();
            }}
            open={showSetDefaultConfirm}
            title="Restore Default Values"
        >
            {null}
        </Modal>
    );

    const VariablesModal = () => (
        <Modal
            className={styles.variablesModal}
            footer={
                <div className={styles.modalActions}>
                    <Button onClick={toggleModal} variant="secondary">
                        Close
                    </Button>
                </div>
            }
            onOpenChange={open => {
                if (!open) setIsOpen(false);
            }}
            open={isOpen}
            size="lg"
            title="Available variables"
        >
            <div className={styles.variablesBody}>
                {listVariables.map((item, index) => (
                    <div className={styles.variableGroup} key={index}>
                        <Heading
                            className={styles.variableGroupTitle}
                            level={5}
                        >
                            {item.label}
                        </Heading>
                        <ul className={styles.variableList}>
                            {item.value.map((subItem, subIndex) => (
                                <li
                                    className={styles.variableRow}
                                    key={subIndex}
                                >
                                    <span className={styles.variableToken}>
                                        {subItem}
                                    </span>
                                    <IconButton
                                        aria-label={`Copy ${subItem}`}
                                        className={styles.variableCopy}
                                        onClick={() => clipboardCopy(subItem)}
                                        size="sm"
                                        type="button"
                                        variant="tertiary"
                                    >
                                        <CopyIcon />
                                    </IconButton>
                                </li>
                            ))}
                        </ul>
                    </div>
                ))}
            </div>
        </Modal>
    );

    return (
        <>
            <UnsavedChangesModal />
            <SetDefaultConfirmModal />
            <VariablesModal />
            <ChooseEmailThemeModal
                handler={handler}
                onApply={handleApplyEmailTheme}
                onOpenChange={setIsThemeModalOpen}
                open={isThemeModalOpen}
                prismicApiEndpoint={inputStore?.prismic_api_endpoint ?? null}
                store={inputStore}
                storeLogo={inputStore?.logo ?? ""}
                storeLogoSmall={inputStore?.logo_small ?? ""}
            />
            <Panel
                className={styles.root}
                header={
                    <div className={styles.headerBar}>
                        <div className={styles.headerText}>
                            <Heading level={4}>Notification settings</Heading>
                        </div>
                        <HeaderActions
                            actions={[
                                {
                                    id: "update",
                                    label: "Update",
                                    icon: shouldShowUpdateIcon ? (
                                        <SaveIcon />
                                    ) : undefined,
                                    loading: shouldShowUpdateSpinner,
                                    disabled: loading || isLoading,
                                    onClick: handleSaveAll,
                                },
                                {
                                    id: "defaults",
                                    label: "Set Defaults",
                                    icon: <RefreshIcon />,
                                    variant: "secondary",
                                    loading: defaultLoading,
                                    disabled: isLoading,
                                    onClick: handleSetDefaultClick,
                                },
                            ]}
                            aria-label="Notification settings actions"
                        />
                    </div>
                }
                showHeaderDivider
            >
                <FormLayout>
                    <FormLayout.Section title="Basic Settings">
                        <Switch
                            checked={Boolean(inputs.email_notification_active)}
                            label="Active"
                            name="email_notification_active"
                            onChange={inputChange}
                        />

                        <FormLayout.Grid columns={2} maxWidth="twoThirds">
                            <Select
                                fullWidth
                                label="Notification Type"
                                onValueChange={value =>
                                    selectChange({ value }, "type")
                                }
                                options={notificationSettingTypeOptions}
                                value={
                                    inputs.type
                                        ? String(inputs.type)
                                        : undefined
                                }
                            />
                            <Input
                                autoComplete="email"
                                fullWidth
                                label="From Email Address"
                                name="from_tx_email"
                                onChange={inputChange}
                                value={inputs.from_tx_email ?? ""}
                            />
                            <Select
                                fullWidth
                                key={selectKey}
                                label="Email Type"
                                onValueChange={handleHandlerChange}
                                options={notificationSettingOptions}
                                value={
                                    inputs.handler
                                        ? String(inputs.handler)
                                        : undefined
                                }
                            />
                            <FormLayout.Field label="If On, Email type will be active">
                                <Switch
                                    checked={Boolean(
                                        inputs.notification_active,
                                    )}
                                    label="If On, Email type will be active"
                                    name="notification_active"
                                    onChange={inputChange}
                                    showLabel={false}
                                />
                            </FormLayout.Field>
                            <FormLayout.Full>
                                <Input
                                    autoComplete="off"
                                    fullWidth
                                    label="Email Subject"
                                    name="email_subject"
                                    onChange={inputChange}
                                    value={inputs.email_subject ?? ""}
                                />
                            </FormLayout.Full>
                        </FormLayout.Grid>

                        {isLoading ? (
                            <div className={styles.loadingInline}>
                                <LoadingProgress
                                    indeterminate
                                    indicator="circular"
                                    size="sm"
                                    title="Loading"
                                />
                            </div>
                        ) : (
                            <>
                                <div className={styles.contentBlock}>
                                    <div className={styles.contentField}>
                                        <Textarea
                                            className={styles.contentTextarea}
                                            fieldClassName={
                                                styles.contentTextareaField
                                            }
                                            fullWidth
                                            label="Content"
                                            modified={
                                                initialState?.content !==
                                                inputs.content
                                            }
                                            name="content"
                                            onChange={inputChange}
                                            rows={20}
                                            textareaClassName={styles.mono}
                                            value={inputs.content ?? ""}
                                        />
                                        <div className={styles.contentActions}>
                                            <Button
                                                className={
                                                    styles.contentActionBtn
                                                }
                                                disabled={
                                                    isOpen || isThemeModalOpen
                                                }
                                                onClick={toggleModal}
                                                size="md"
                                                variant="secondary"
                                            >
                                                View Variables
                                            </Button>
                                            <Button
                                                className={
                                                    styles.contentActionBtn
                                                }
                                                disabled={
                                                    isOpen ||
                                                    isThemeModalOpen ||
                                                    !hasEmailThemeTemplate(
                                                        handler,
                                                    )
                                                }
                                                onClick={() =>
                                                    setIsThemeModalOpen(true)
                                                }
                                                size="md"
                                                variant="secondary"
                                            >
                                                Choose theme
                                            </Button>
                                        </div>
                                    </div>
                                </div>

                                <div className={styles.previewBlock}>
                                    <HtmlPreview
                                        height={1200}
                                        html={previewHtml}
                                        title="Email Preview"
                                    />
                                </div>
                            </>
                        )}
                    </FormLayout.Section>
                </FormLayout>
            </Panel>
        </>
    );
};

export default NotificationSettingForm;
